import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Wheat, LogOut, Printer, Pencil, Search, Plus, RefreshCw,
  CalendarDays, Save, BookOpen, Package, FileSpreadsheet, AlertTriangle, Calculator as CalcIcon,
  DollarSign, Building2, Layers, Settings as SettingsIcon,
} from "lucide-react";
import RecipeEditor from "@/components/RecipeEditor";
import PrintReport from "@/components/PrintReport";
import LotManager from "@/components/LotManager";

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Dashboard() {
  const { authAxios, logout, username } = useAuth();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || todayISO();
  const [recipes, setRecipes] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [stocks, setStocks] = useState({}); // material_name -> {initial_stock, entries}
  const [reportDate, setReportDate] = useState(initialDate);
  const [search, setSearch] = useState("");
  const [editorRecipe, setEditorRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [lotMaterial, setLotMaterial] = useState(null);
  const [lotsSummary, setLotsSummary] = useState({}); // material_name -> {total_remaining, total_original}

  const loadLotsSummary = async () => {
    try {
      const res = await authAxios.get("/lots");
      const summary = {};
      for (const lot of res.data) {
        const k = lot.material_name;
        if (!summary[k]) summary[k] = { remaining: 0, original: 0, consumed: 0, lots_count: 0, active_lot: null };
        summary[k].remaining += lot.remaining_qty || 0;
        summary[k].original += lot.original_qty || 0;
        summary[k].consumed += lot.consumed_qty || 0;
        summary[k].lots_count += 1;
        if (!summary[k].active_lot && lot.remaining_qty > 0.0001) {
          summary[k].active_lot = lot.lot_number;
          summary[k].active_remaining = lot.remaining_qty;
        }
      }
      setLotsSummary(summary);
    } catch (err) {
      // silent
    }
  };

  const loadRecipes = async () => {
    try {
      const res = await authAxios.get("/recipes");
      setRecipes(res.data);
    } catch (err) {
      toast.error("Eroare la încărcare rețete");
    } finally {
      setLoading(false);
    }
  };

  const loadReportForDate = async (d) => {
    try {
      const res = await authAxios.get(`/reports/by-date/${d}`);
      if (res.data && res.data.id) {
        const q = {};
        for (const p of res.data.products) q[p.recipe_id] = String(p.quantity);
        setQuantities(q);
        const s = {};
        for (const m of res.data.materials) {
          s[m.name] = {
            initial_stock: String(m.initial_stock || 0),
            entries: String(m.entries || 0),
          };
        }
        setStocks(s);
        toast.info(`Raport încărcat: ${d}`);
      } else {
        // No saved report → pre-fill initial stocks from auto-initial endpoint
        setQuantities({});
        try {
          const auto = await authAxios.get(`/reports/auto-initial/${d}`);
          const s = {};
          const stocksMap = auto.data?.stocks || {};
          for (const [name, val] of Object.entries(stocksMap)) {
            s[name] = { initial_stock: String(val), entries: "" };
          }
          setStocks(s);
          if (auto.data?.previous_report_date) {
            toast.info(`Stocuri preluate din ${auto.data.previous_report_date}`);
          } else if (auto.data?.has_monthly_inventory) {
            toast.info("Stocuri inițiale preluate din inventarul lunar");
          }
        } catch {
          setStocks({});
        }
      }
    } catch (err) {
      setQuantities({});
      setStocks({});
    }
  };

  useEffect(() => {
    loadRecipes();
    loadReportForDate(reportDate);
    loadLotsSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecipes = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return recipes;
    return recipes.filter((r) => {
      if (r.name.toLowerCase().includes(s)) return true;
      // Search by ingredient name too
      return (r.ingredients || []).some((ing) =>
        (ing.name || "").toLowerCase().includes(s)
      );
    });
  }, [recipes, search]);

  const calculation = useMemo(() => {
    const materials = {};
    const products = [];
    for (const r of recipes) {
      const q = parseFloat(quantities[r.id]);
      if (!q || q <= 0) continue;
      products.push({ recipe_id: r.id, name: r.name, unit: r.unit, quantity: q });
      for (const ing of r.ingredients) {
        const key = ing.name.trim().toLowerCase();
        materials[key] = (materials[key] || 0) + ing.qty_per_unit * q;
      }
    }
    const list = Object.entries(materials)
      .map(([name, total]) => ({ name, total: Math.round(total * 10000) / 10000 }))
      .sort((a, b) => b.total - a.total);
    return { materials: list, products };
  }, [recipes, quantities]);

  // Compute stock summary for each material
  const stockData = useMemo(() => {
    return calculation.materials.map((m) => {
      const st = stocks[m.name] || {};
      const initial = parseFloat(st.initial_stock) || 0;
      // Prefer lot-based entries (total received from lots), fallback to manual entries
      const lotInfo = lotsSummary[m.name];
      const entries = lotInfo ? lotInfo.original : (parseFloat(st.entries) || 0);
      const final_stock = Math.round((initial + entries - m.total) * 10000) / 10000;
      return {
        name: m.name,
        consumption: m.total,
        initial_stock: initial,
        entries: entries,
        final_stock,
      };
    });
  }, [calculation.materials, stocks, lotsSummary]);

  const LOW_STOCK_THRESHOLD = 5; // kg
  const stockAlerts = useMemo(() => {
    const negative = stockData.filter((m) => m.final_stock < 0);
    const low = stockData.filter((m) => m.final_stock >= 0 && m.final_stock < LOW_STOCK_THRESHOLD);
    return { negative, low };
  }, [stockData]);

  const onSetQty = (id, val) => {
    setQuantities((prev) => ({ ...prev, [id]: val }));
  };

  const onSetStock = (matName, key, val) => {
    setStocks((prev) => ({
      ...prev,
      [matName]: { ...(prev[matName] || {}), [key]: val },
    }));
  };

  const onClearAll = () => {
    setQuantities({});
    setStocks({});
    toast.info("Cantități și stocuri resetate");
  };

  const onSaveDay = async () => {
    setSaving(true);
    try {
      await authAxios.post("/reports", {
        date: reportDate,
        products: calculation.products,
        materials: stockData,
      });
      await loadLotsSummary();
      toast.success(`Raport salvat pentru ${reportDate}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const onDownloadExcel = async () => {
    try {
      // Save first if not already saved
      await authAxios.post("/reports", {
        date: reportDate,
        products: calculation.products,
        materials: stockData,
      });
      const res = await authAxios.get(`/reports/by-date/${reportDate}/excel`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `raport_${reportDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Excel descărcat");
    } catch (err) {
      toast.error("Eroare la descărcare Excel");
    }
  };

  const onSaveRecipe = async (recipe) => {
    try {
      if (recipe.id) {
        const res = await authAxios.put(`/recipes/${recipe.id}`, {
          name: recipe.name,
          unit: recipe.unit,
          ingredients: recipe.ingredients,
        });
        setRecipes((prev) =>
          [...prev.map((r) => (r.id === recipe.id ? res.data : r))]
            .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
        );
        toast.success("Rețetă actualizată");
      } else {
        const res = await authAxios.post(`/recipes`, {
          name: recipe.name,
          unit: recipe.unit,
          ingredients: recipe.ingredients,
        });
        setRecipes((prev) =>
          [...prev, res.data].sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
        );
        toast.success("Rețetă creată");
      }
      setEditorRecipe(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    }
  };

  const onDeleteRecipe = async (id) => {
    if (!confirm("Ștergi această rețetă?")) return;
    try {
      await authAxios.delete(`/recipes/${id}`);
      setRecipes((prev) => prev.filter((r) => r.id !== id));
      setQuantities((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Rețetă ștearsă");
      setEditorRecipe(null);
    } catch (err) {
      toast.error("Eroare la ștergere");
    }
  };

  const totalSelected = calculation.products.length;

  const handleDateChange = async (d) => {
    setReportDate(d);
    await loadReportForDate(d);
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-neutral-50">
      {/* SIDEBAR */}
      <aside className="no-print w-[260px] shrink-0 border-r border-neutral-200 bg-white h-screen sticky top-0 flex flex-col" data-testid="sidebar">
        <div className="px-5 pt-6 pb-4 border-b border-neutral-100">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <Wheat className="w-5 h-5 text-amber-600" strokeWidth={1.75} />
            </div>
            <div>
              <h1 className="text-sm font-bold text-neutral-900 leading-tight">Brutăria LaDenis</h1>
              <p className="text-[11px] text-neutral-500 leading-tight">SC AXA OMEGA</p>
            </div>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="recipe-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută rețetă sau ingredient..."
              className="w-full rounded-md border border-neutral-200 bg-neutral-50 pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white"
            />
          </div>
        </div>

        <div className="px-3 pt-3 pb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 px-2">
            Rețete ({filteredRecipes.length})
          </span>
          <button
            data-testid="new-recipe-button"
            onClick={() => setEditorRecipe({ name: "", unit: "buc", ingredients: [] })}
            className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-md p-1 transition-colors"
            title="Rețetă nouă"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll px-2 pb-3" data-testid="recipe-list">
          {loading && <div className="text-center text-xs text-neutral-400 py-6">Se încarcă...</div>}
          {!loading && filteredRecipes.map((r) => {
            const active = (quantities[r.id] && parseFloat(quantities[r.id]) > 0);
            const searchTerm = search.trim().toLowerCase();
            const nameMatch = searchTerm && r.name.toLowerCase().includes(searchTerm);
            const matchedIngredient = searchTerm && !nameMatch
              ? (r.ingredients || []).find((ing) => (ing.name || "").toLowerCase().includes(searchTerm))
              : null;
            return (
              <div
                key={r.id}
                className={`group flex items-center justify-between px-2.5 py-1.5 rounded-md ${active ? "bg-amber-50" : "hover:bg-neutral-50"}`}
                data-testid={`recipe-item-${r.id}`}
              >
                <button
                  onClick={() => setEditorRecipe(r)}
                  data-testid={`recipe-row-${r.id}`}
                  className="flex-1 min-w-0 text-left cursor-pointer"
                  title="Click pentru a edita rețeta"
                >
                  <div className={`text-sm truncate ${active ? "text-amber-900 font-semibold" : "text-neutral-700 hover:text-amber-700"}`}>
                    {r.name}
                  </div>
                  {matchedIngredient ? (
                    <div className="text-[10px] text-emerald-700 truncate" title={matchedIngredient.name}>
                      ↳ <span className="capitalize">{matchedIngredient.name}</span>
                    </div>
                  ) : (
                    <div className="text-[10px] text-neutral-400 uppercase tracking-wider">
                      {r.unit} · {r.ingredients.length} ing.
                    </div>
                  )}
                </button>
                <button
                  data-testid={`edit-recipe-${r.id}`}
                  onClick={() => setEditorRecipe(r)}
                  className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 p-1.5 rounded transition-colors ml-1"
                  title="Editează rețeta"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
          {!loading && filteredRecipes.length === 0 && (
            <div className="text-center text-xs text-neutral-400 py-6">Nicio rețetă găsită</div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-neutral-100 space-y-2">
          <Link
            to="/inventar"
            data-testid="inventory-link"
            className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
          >
            <Package className="w-3.5 h-3.5" />
            Inventar lunar
          </Link>
          <Link
            to="/calculator"
            data-testid="calculator-link"
            className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
          >
            <CalcIcon className="w-3.5 h-3.5" />
            Calculator rețetă
          </Link>
          <Link
            to="/rapoarte"
            data-testid="reports-link"
            className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Rapoarte salvate
          </Link>

          <div className="border-t border-neutral-200 pt-2 mt-1 space-y-2">
            <div className="text-[9px] uppercase tracking-wider text-neutral-400 font-semibold px-0.5">Module suplimentare</div>
            <a
              href="/cost.html"
              data-testid="link-cost"
              className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
            >
              <DollarSign className="w-3.5 h-3.5" />
              Costuri
            </a>
            <a
              href="/firme.html"
              data-testid="link-firme"
              className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
            >
              <Building2 className="w-3.5 h-3.5" />
              Firme / Superfirma
            </a>
            <a
              href="/loturi.html"
              data-testid="link-loturi"
              className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
            >
              <Layers className="w-3.5 h-3.5" />
              Loturi (pagină)
            </a>
            <a
              href="/setari.html"
              data-testid="link-setari"
              className="w-full inline-flex items-center gap-2 text-xs text-neutral-700 hover:text-amber-700 font-medium transition-colors"
            >
              <SettingsIcon className="w-3.5 h-3.5" />
              Setări
            </a>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-neutral-900 transition-colors mt-1 border-t border-neutral-200 pt-2"
          >
            <LogOut className="w-3.5 h-3.5" />
            Deconectare ({username})
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="no-print flex-1 overflow-y-auto h-screen thin-scroll">
        <header className="sticky top-0 z-10 bg-neutral-50/95 backdrop-blur border-b border-neutral-200 px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-neutral-900">Producție Zi</h2>
              <p className="text-xs text-neutral-500 mt-0.5">Introdu cantitățile produselor și stocurile materiilor prime</p>
            </div>
            <div className="flex items-center gap-2 pl-5 border-l border-neutral-200">
              <CalendarDays className="w-4 h-4 text-amber-600" />
              <input
                data-testid="report-date-input"
                type="date"
                value={reportDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="rounded-md border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="clear-quantities-button"
              onClick={onClearAll}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Resetează
            </button>
            <button
              data-testid="save-day-button"
              onClick={onSaveDay}
              disabled={saving || totalSelected === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "Se salvează..." : "Salvează ziua"}
            </button>
            <button
              data-testid="download-excel-button"
              onClick={onDownloadExcel}
              disabled={totalSelected === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-xs font-semibold text-emerald-800 transition-colors"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              Excel
            </button>
            <button
              data-testid="print-button"
              onClick={() => setShowPrint(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              Tipărește raport
            </button>
          </div>
        </header>

        <div className="px-8 py-6 space-y-6">
          {/* Stock alerts banner */}
          {(stockAlerts.negative.length > 0 || stockAlerts.low.length > 0) && (
            <div
              data-testid="stock-alerts-banner"
              className={`rounded-xl border p-4 flex items-start gap-3 ${stockAlerts.negative.length > 0 ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
            >
              <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${stockAlerts.negative.length > 0 ? "text-red-600" : "text-amber-600"}`} />
              <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-bold ${stockAlerts.negative.length > 0 ? "text-red-900" : "text-amber-900"}`}>
                  Atenție: materii prime cu stoc {stockAlerts.negative.length > 0 ? "negativ" : "scăzut"}
                </h4>
                <p className="text-xs text-neutral-700 mt-0.5">
                  {stockAlerts.negative.length > 0 && (
                    <span><strong className="text-red-700">{stockAlerts.negative.length}</strong> sub 0 kg: <span className="capitalize">{stockAlerts.negative.slice(0, 5).map((m) => m.name).join(", ")}</span>{stockAlerts.negative.length > 5 ? "..." : ""}. </span>
                  )}
                  {stockAlerts.low.length > 0 && (
                    <span><strong className="text-amber-700">{stockAlerts.low.length}</strong> sub {LOW_STOCK_THRESHOLD} kg: <span className="capitalize">{stockAlerts.low.slice(0, 5).map((m) => m.name).join(", ")}</span>{stockAlerts.low.length > 5 ? "..." : ""}.</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Products */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between bg-neutral-50/50">
              <h3 className="text-sm font-bold text-neutral-900">Produse</h3>
              <span className="text-xs text-neutral-500" data-testid="selected-count">{totalSelected} produse selectate</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wider uppercase text-neutral-500 bg-neutral-50">
                    <th className="px-5 py-2.5 w-12">#</th>
                    <th className="px-5 py-2.5">Produs</th>
                    <th className="px-5 py-2.5 w-24">U.M.</th>
                    <th className="px-5 py-2.5 w-32">Ingrediente</th>
                    <th className="px-5 py-2.5 w-40 text-right">Cantitate</th>
                  </tr>
                </thead>
                <tbody data-testid="products-table-body">
                  {filteredRecipes.map((r, idx) => {
                    const qty = quantities[r.id] || "";
                    return (
                      <tr key={r.id} className="border-t border-neutral-100 hover:bg-amber-50/30 transition-colors">
                        <td className="px-5 py-2 text-neutral-400 text-xs tabular-nums">{idx + 1}</td>
                        <td className="px-5 py-2 text-neutral-900 font-medium">{r.name}</td>
                        <td className="px-5 py-2 text-neutral-500 text-xs">{r.unit}</td>
                        <td className="px-5 py-2 text-neutral-400 text-xs">{r.ingredients.length}</td>
                        <td className="px-5 py-2 text-right">
                          <input
                            data-testid={`qty-input-${r.id}`}
                            type="number"
                            min="0"
                            step="any"
                            value={qty}
                            onChange={(e) => onSetQty(r.id, e.target.value)}
                            placeholder="0"
                            className={`w-28 text-right rounded-md border px-2.5 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors ${qty && parseFloat(qty) > 0 ? "border-amber-500 bg-amber-50 font-semibold text-amber-900" : "border-neutral-200 bg-white text-neutral-700"}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stock table */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between bg-emerald-50/40">
              <div>
                <h3 className="text-sm font-bold text-neutral-900">Stocuri materii prime · {reportDate}</h3>
                <p className="text-[11px] text-neutral-500 mt-0.5">Stoc final = Stoc inițial + Intrări − Consum</p>
              </div>
              <span className="text-xs text-neutral-500">{stockData.length} materii prime</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wider uppercase text-neutral-500 bg-neutral-50">
                    <th className="px-5 py-2.5 w-10">#</th>
                    <th className="px-5 py-2.5">Materie primă</th>
                    <th className="px-5 py-2.5 w-32 text-right">Stoc inițial</th>
                    <th className="px-5 py-2.5 w-32 text-right">Loturi · Rămas</th>
                    <th className="px-5 py-2.5 w-32 text-right">Consum</th>
                    <th className="px-5 py-2.5 w-32 text-right">Stoc final</th>
                  </tr>
                </thead>
                <tbody data-testid="stock-table-body">
                  {stockData.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-xs text-neutral-400">
                        Introdu cantități în tabelul de mai sus pentru a vedea stocurile.
                      </td>
                    </tr>
                  )}
                  {stockData.map((m, idx) => {
                    const negStock = m.final_stock < 0;
                    const lowStock = !negStock && m.final_stock < LOW_STOCK_THRESHOLD;
                    const rowClass = negStock ? "bg-red-50/40" : lowStock ? "bg-amber-50/40" : "";
                    return (
                      <tr key={m.name} className={`border-t border-neutral-100 ${rowClass}`}>
                        <td className="px-5 py-1.5 text-neutral-400 text-xs">{idx + 1}</td>
                        <td className="px-5 py-1.5 text-neutral-900 capitalize flex items-center gap-2">
                          {(negStock || lowStock) && (
                            <AlertTriangle className={`w-3 h-3 ${negStock ? "text-red-600" : "text-amber-600"}`} />
                          )}
                          {m.name}
                        </td>
                        <td className="px-5 py-1.5 text-right">
                          <input
                            data-testid={`stock-initial-${m.name.replace(/\s/g, "-")}`}
                            type="number"
                            step="any"
                            min="0"
                            value={stocks[m.name]?.initial_stock || ""}
                            onChange={(e) => onSetStock(m.name, "initial_stock", e.target.value)}
                            placeholder="0"
                            className="w-24 text-right rounded border border-neutral-200 px-2 py-1 text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </td>
                        <td className="px-5 py-1.5 text-right">
                          <button
                            data-testid={`lot-button-${m.name.replace(/\s/g, "-")}`}
                            onClick={() => setLotMaterial(m.name)}
                            className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-white hover:bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 transition-colors"
                            title="Gestionează loturi"
                          >
                            {(() => {
                              const lotInfo = lotsSummary[m.name];
                              if (lotInfo && lotInfo.remaining > 0) {
                                return (
                                  <>
                                    <span className="tabular-nums">{lotInfo.remaining.toFixed(2)}</span>
                                    <span className="text-[9px] text-neutral-500 font-normal">/lot{lotInfo.active_lot}</span>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <Package className="w-3 h-3" />
                                  <span>+lot</span>
                                </>
                              );
                            })()}
                          </button>
                        </td>
                        <td className="px-5 py-1.5 text-right text-amber-700 font-semibold tabular-nums">
                          {m.consumption.toFixed(3)}
                        </td>
                        <td className={`px-5 py-1.5 text-right tabular-nums font-bold ${negStock ? "text-red-600" : lowStock ? "text-amber-700" : "text-emerald-700"}`}>
                          {m.final_stock.toFixed(3)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* RIGHT REPORT PANEL */}
      <aside className="no-print w-[300px] shrink-0 border-l border-neutral-200 bg-white h-screen sticky top-0 flex flex-col" data-testid="report-panel">
        <div className="px-5 pt-6 pb-3 border-b border-neutral-100">
          <h3 className="text-sm font-bold text-neutral-900">Raport Materii Prime</h3>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Consum agregat pentru cantitățile introduse
          </p>
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll px-5 py-4" data-testid="materials-list">
          {calculation.materials.length === 0 && (
            <div className="text-center py-12 px-3">
              <div className="w-10 h-10 mx-auto rounded-full bg-neutral-100 flex items-center justify-center mb-3">
                <Wheat className="w-5 h-5 text-neutral-400" />
              </div>
              <p className="text-xs text-neutral-500">
                Introdu cantități în tabel pentru a vedea consumul.
              </p>
            </div>
          )}
          {calculation.materials.map((m, idx) => (
            <div
              key={m.name}
              className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0"
              data-testid={`material-row-${m.name.replace(/\s/g, '-')}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] text-neutral-400 tabular-nums w-5">{idx + 1}</span>
                <span className="text-sm text-neutral-700 capitalize truncate">{m.name}</span>
              </div>
              <span className="text-sm font-semibold text-amber-700 tabular-nums">
                {m.total.toFixed(3)} <span className="text-[10px] text-neutral-400 font-normal">kg</span>
              </span>
            </div>
          ))}
        </div>
        {calculation.products.length > 0 && (
          <div className="px-5 py-3 border-t border-neutral-100 bg-amber-50/40">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-neutral-600 font-semibold uppercase tracking-wider">Total ingrediente</span>
              <span className="text-sm font-bold text-amber-800 tabular-nums">{calculation.materials.length}</span>
            </div>
          </div>
        )}
      </aside>

      {showPrint && (
        <PrintReport
          calculation={calculation}
          stockData={stockData}
          reportDate={reportDate}
          lotsSummary={lotsSummary}
          onClose={() => setShowPrint(false)}
        />
      )}

      {editorRecipe && (
        <RecipeEditor
          recipe={editorRecipe}
          onClose={() => setEditorRecipe(null)}
          onSave={onSaveRecipe}
          onDelete={editorRecipe.id ? () => onDeleteRecipe(editorRecipe.id) : null}
        />
      )}

      {lotMaterial && (
        <LotManager
          authAxios={authAxios}
          materialName={lotMaterial}
          defaultDate={reportDate}
          onClose={() => setLotMaterial(null)}
          onChange={loadLotsSummary}
        />
      )}
    </div>
  );
}
